import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Collapse } from "react-bootstrap";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { fetchProfileDiffData } from "../../../../redux/slice/profileUpdateSupportSlice";
import { fetchUserById } from "../../../../redux/slice/getUserByIdSlice";

const ProfileUpdateSection = ({
  hasCategory = () => true,
  openSections = {},
  toggleSection = () => { },
  InfoRow,
  InfoRowPersonal,
  ticket,
}) => {
  const dispatch = useDispatch();

  const profileDiffData = useSelector((state) => state.profileUpdate?.profileDiffData || []);
  console.log("profile diff. data:", profileDiffData);
  const { User } = useSelector(state => state?.User);
  const ticketId = ticket?.id;
  const isUpdated = ticket?.status?.toLowerCase() === "updated";
  const hitDiffApi = ticket?.status?.toLowerCase() === "verified" || ticket?.status?.toLowerCase() === "closed";
  const hitIniitalProfileData = ticket?.status?.toLowerCase() === "created" || ticket?.status?.toLowerCase() === "in_progress" || ticket?.status?.toLowerCase() === "approved";

  useEffect(() => {
    if (!ticketId) return;
    if (hitIniitalProfileData) {
      dispatch(fetchUserById(ticket?.createdByUserId));
    } else if (!hitDiffApi) {
      dispatch(fetchProfileDiffData(ticketId));
    }

  }, [dispatch, ticketId, hitIniitalProfileData, hitDiffApi, ticket?.userId]);

  const approvedSections =
    ticket?.fieldApprovals
      ?.filter((item) => item?.isApproved)
      ?.map((item) => item?.fieldGroupName) || [];

  const hasApprovedCategory = (section) =>
    approvedSections.includes(section);

  const shouldShowSection = (sectionName) => {
    if (isUpdated) {
      return hasApprovedCategory(sectionName);
    }

    return hasCategory(sectionName);
  };

  // =========================
  // Helpers
  // =========================

  const getSectionDiffData = (sectionType) => {
    return (
      profileDiffData?.find(
        (item) => item?.sectionType === sectionType
      ) || {}
    );
  };

  // Maps backend sub-object keys for multiRecord: true types
  const listKeyMap = {
    "INSURANCE_DEPENDENT": "insuranceDependentDetails",
    "NOMINEE": "nomineeDetails",
    "EDUCATION": "educationDetails",
    "EXPERIENCE": "employmentHistory",
    "ADDRESS": "addresses",
    "PROFESSIONAL_REFERENCE": "professional"
  };

  const getCurrentData = (sectionType) => {
    if (hitIniitalProfileData) {
      switch (sectionType) {
        case "PERSONAL_INFORMATION":
          return User?.personalDetail || {};
        case "BANK_DETAILS":
          return User?.bankDetails || {};
        case "NOMINEE":
          // Keep multi-record sections consistent as arrays or wrapped objects
          return User?.nomineeDetails || [];
        case "ADDITIONAL_DETAILS":
          return User?.additionalDetails || {};
        case "INSURANCE_DEPENDENT":
          return User?.insuranceDependentDetails || [];
        case "ADDRESS":
          return User?.addresses || [];
        case "EDUCATION":
          return User?.educationDetails || [];
        case "EXPERIENCE":
          return User?.employmentHistory || [];
        case "PROFESSIONAL_REFERENCE":
          return User?.professionalReferences || [];
        default:
          return {};
      }
    }

    // --- DIFF TICKET HANDLING ---
    const diffItem = getSectionDiffData(sectionType);

    if (diffItem?.multiRecord) {
      const list = diffItem?.currentList || [];
      const nestedKey = listKeyMap[sectionType];

      if (list.length === 0) return [];

      // Check if the first item has the nested key wrapper
      if (nestedKey && list[0] && Object.prototype.hasOwnProperty.call(list[0], nestedKey)) {
        return list.flatMap(item => item[nestedKey] || []);
      }

      // CRITICAL FIX: If no nested key wrapper is found, the list is ALREADY flat. 
      // Return it directly so it doesn't get ignored or swallowed.
      return list;
    }

    return diffItem?.currentData || {};
  };

  const getProposedData = (sectionType) => {
    if (hitIniitalProfileData) {
      const listSections = ["NOMINEE", "INSURANCE_DEPENDENT", "ADDRESS", "EDUCATION", "EXPERIENCE"];
      return listSections.includes(sectionType) ? [] : {};
    }

    const diffItem = getSectionDiffData(sectionType);

    if (diffItem?.multiRecord) {
      const list = diffItem?.proposedList || [];
      const nestedKey = listKeyMap[sectionType];

      if (list.length === 0) {
        if (nestedKey && diffItem[nestedKey]) return diffItem[nestedKey];
        return [];
      }

      if (nestedKey && list[0] && Object.prototype.hasOwnProperty.call(list[0], nestedKey)) {
        return list.flatMap(item => item[nestedKey] || []);
      }
      return list;
    }

    return diffItem?.proposedData || {};
  };

  const formatDate = (date) => {
    if (!date) return "—";

    return new Date(date).toLocaleDateString();
  };

  const extractYear = (date) => {
    if (!date) return "—";

    return new Date(date).getFullYear();
  };

  const getFormattedDuration = (from, to) => {
    return `${extractYear(from)} - ${extractYear(to)}`;
  };

  const joinAddress = (address) => {
    if (!address) return "—";

    return [
      address?.address1,
      address?.address2,
      address?.address3,
      address?.city,
      address?.state,
      address?.zip,
    ]
      .filter(Boolean)
      .join(", ");
  };

  // =========================
  // Section Data
  // =========================

  const personalCurrentData = getCurrentData("PERSONAL_INFORMATION");
  const personalProposedData = getProposedData("PERSONAL_INFORMATION");

  const currentBankData = getCurrentData("BANK_DETAILS");
  const proposedBankData = getProposedData("BANK_DETAILS");

  const familyCurrentData = getCurrentData("INSURANCE_DEPENDENT");
  const familyProposedData = getProposedData("INSURANCE_DEPENDENT");
  console.log("current data of insuranceDependentDetails:", familyCurrentData);

  const nomineeCurrentData = getCurrentData("NOMINEE");
  console.log("current data of nominee:", nomineeCurrentData);
  const nomineeProposedData = getProposedData("NOMINEE");

  const educationCurrentData = getCurrentData("EDUCATION");
  const educationProposedData = getProposedData("EDUCATION");

  const experienceCurrentData = getCurrentData("EXPERIENCE");
  const experienceProposedData = getProposedData("EXPERIENCE");

  const rawAddressCurrent = getCurrentData("ADDRESS");
  const addressCurrentData = Array.isArray(rawAddressCurrent) ? rawAddressCurrent : (rawAddressCurrent?.addresses || []);
  const addressProposedData = getProposedData("ADDRESS");

  const additionalCurrentData = getCurrentData("ADDITIONAL_DETAILS");
  const additionalProposedData = getProposedData("ADDITIONAL_DETAILS");

  const referenceCurrentData = getCurrentData("PROFESSIONAL_REFERENCE");
  const referenceProposedData = getProposedData("PROFESSIONAL_REFERENCE");

  // const referenceCurrentData = additionalCurrentData?.professionalReferences || [];
  // const referenceProposedData = additionalProposedData?.professionalReferences || [];

  // =========================
  // Reusable Card
  // =========================

  const SectionCard = ({
    title,
    sectionKey,
    children,
  }) => (
    <Card className="shadow-sm border-1 rounded-3 mb-3">
      <Card.Body className="p-0">
        <Row
          className="align-items-center m-0 cursor-pointer"
          onClick={() => toggleSection(sectionKey)}
        >
          <Col className="col-heading p-3">
            <h5 className="mb-0">{title}</h5>
          </Col>

          <Col xs="auto" className="d-flex align-items-center gap-3">
            {openSections[sectionKey] ? (
              <BsChevronUp size={16} />
            ) : (
              <BsChevronDown size={16} />
            )}
          </Col>

        </Row>

        <Collapse in={openSections[sectionKey]}>
          <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-4">
            <hr className="mt-0 mb-3" />
            {children}
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );

  return (
    <div className="timeline-content p-3">
      {hitDiffApi ? <p className="text-center text-danger py-2">Employee Profile has been updated successfully and you can see it in their profile pages!</p> : (
        <>
          {/* ================= PERSONAL ================= */}

          {shouldShowSection("PERSONAL_INFORMATION") && (
            <>
              <SectionCard
                title="Personal Information (Current)"
                sectionKey="personal"
              >
                <div className="row">
                  <InfoRowPersonal
                    label="Father"
                    value={personalCurrentData?.fatherName}
                  />
                  <InfoRowPersonal
                    label="Mother"
                    value={personalCurrentData?.motherName}
                  />
                  <InfoRowPersonal
                    label="Aadhaar"
                    value={personalCurrentData?.adhaarNo}
                  />
                  <InfoRowPersonal
                    label="PAN"
                    value={personalCurrentData?.panNo}
                  />
                  <InfoRowPersonal
                    label="UAN"
                    value={personalCurrentData?.uanNo}
                  />
                  <InfoRowPersonal
                    label="Religion"
                    value={personalCurrentData?.religion}
                  />
                  <InfoRowPersonal
                    label="Blood Group"
                    value={personalCurrentData?.bloodGroup}
                  />
                  <InfoRowPersonal
                    label="Gender"
                    value={personalCurrentData?.gender}
                  />
                  <InfoRowPersonal
                    label="Marital Status"
                    value={personalCurrentData?.maritalStatus}
                  />
                  <InfoRowPersonal
                    label="Employment of Spouse"
                    value={personalCurrentData?.employmentOfSpouse}
                  />
                  <InfoRowPersonal
                    label="No. of Children"
                    value={personalCurrentData?.noOfChildren}
                  />
                  <InfoRowPersonal
                    label="Nationality"
                    value={personalCurrentData?.nationality}
                  />
                  <InfoRowPersonal
                    label="Alternative Mob. No."
                    value={personalCurrentData?.secondaryMobile}
                  />
                </div>
              </SectionCard>

              <SectionCard
                title="Personal Information (Proposed)"
                sectionKey="personal"
              >
                <div className="row">
                  <InfoRowPersonal
                    label="Father"
                    value={personalProposedData?.fatherName}
                  />
                  <InfoRowPersonal
                    label="Mother"
                    value={personalProposedData?.motherName}
                  />
                  <InfoRowPersonal
                    label="Aadhaar"
                    value={personalProposedData?.adhaarNo}
                  />
                  <InfoRowPersonal
                    label="PAN"
                    value={personalProposedData?.panNo}
                  />
                  <InfoRowPersonal
                    label="UAN"
                    value={personalProposedData?.uanNo}
                  />
                  <InfoRowPersonal
                    label="Religion"
                    value={personalProposedData?.religion}
                  />
                  <InfoRowPersonal
                    label="Blood Group"
                    value={personalProposedData?.bloodGroup}
                  />
                  <InfoRowPersonal
                    label="Gender"
                    value={personalProposedData?.gender}
                  />
                  <InfoRowPersonal
                    label="Marital Status"
                    value={personalProposedData?.maritalStatus}
                  />
                  <InfoRowPersonal
                    label="Employment of Spouse"
                    value={personalProposedData?.employmentOfSpouse}
                  />
                  <InfoRowPersonal
                    label="No. of Children"
                    value={personalProposedData?.noOfChildren}
                  />
                  <InfoRowPersonal
                    label="Nationality"
                    value={personalProposedData?.nationality}
                  />
                  <InfoRowPersonal
                    label="Alternative Mob. No."
                    value={personalProposedData?.secondaryMobile}
                  />
                </div>
              </SectionCard>
            </>
          )}

          {/* ================= BANK ================= */}

          {shouldShowSection("BANK_DETAILS") && (
            <>
              <SectionCard
                title="Bank Information (Current)"
                sectionKey="bank"
              >
                <InfoRow
                  label="Account Holder Name"
                  value={currentBankData?.accountHolderName}
                />
                <InfoRow
                  label="Account Number"
                  value={currentBankData?.accountNumber}
                />
                <InfoRow
                  label="Bank Name"
                  value={currentBankData?.bankName}
                />
                <InfoRow
                  label="IFSC Code"
                  value={currentBankData?.ifscCode}
                />
                <InfoRow
                  label="Branch Name"
                  value={currentBankData?.branchName}
                />
              </SectionCard>

              <SectionCard
                title="Bank Information (Proposed)"
                sectionKey="bank"
              >
                <InfoRow
                  label="Account Holder Name"
                  value={proposedBankData?.accountHolderName}
                />
                <InfoRow
                  label="Account Number"
                  value={proposedBankData?.accountNumber}
                />
                <InfoRow
                  label="Bank Name"
                  value={proposedBankData?.bankName}
                />
                <InfoRow
                  label="IFSC Code"
                  value={proposedBankData?.ifscCode}
                />
                <InfoRow
                  label="Branch Name"
                  value={proposedBankData?.branchName}
                />
              </SectionCard>
            </>
          )}

          {/* ================= FAMILY ================= */}

          {shouldShowSection("INSURANCE_DEPENDENT") && (
            <>
              <SectionCard
                title="Insurance Dependent Details (Current)"
                sectionKey="family"
              >
                {Array.isArray(familyCurrentData) && familyCurrentData.length > 0 ? (
                  familyCurrentData.map((member, index) => (
                    <div key={member?.id || index}>
                      <div className="row">
                        <InfoRowPersonal
                          label="Name"
                          value={member?.name}
                        />

                        <InfoRowPersonal
                          label="Relationship"
                          value={member?.relationship}
                        />

                        <InfoRowPersonal
                          label="DOB"
                          value={formatDate(member?.dob)}
                        />

                        <InfoRowPersonal
                          label="Gender"
                          value={member?.gender}
                        />

                        <InfoRowPersonal
                          label="Age"
                          value={member?.age}
                        />

                        <InfoRowPersonal
                          label="Phone"
                          value={member?.phone}
                        />

                        <InfoRowPersonal
                          label="SI Amount"
                          value={member?.amount}
                        />
                      </div>

                      <hr />
                    </div>
                  )
                  )) : (
                  <div className="text-muted text-center py-2">
                    No current Insurance Dependent Details available
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Insurance Dependent Details (Proposed)"
                sectionKey="family"
              >
                {Array.isArray(familyProposedData) && familyProposedData.length > 0 ? (
                  familyProposedData.map((member, index) => (
                    <div key={member?.id || index}>
                      <div className="row">
                        <InfoRowPersonal
                          label="Name"
                          value={member?.name}
                        />

                        <InfoRowPersonal
                          label="Relationship"
                          value={member?.relationship}
                        />

                        <InfoRowPersonal
                          label="DOB"
                          value={formatDate(member?.dob)}
                        />

                        <InfoRowPersonal
                          label="Gender"
                          value={member?.gender}
                        />

                        <InfoRowPersonal
                          label="Age"
                          value={member?.age}
                        />

                        <InfoRowPersonal
                          label="Phone"
                          value={member?.phone}
                        />

                        <InfoRowPersonal
                          label="SI Amount"
                          value={member?.amount}
                        />
                      </div>

                      <hr />
                    </div>
                  ))
                ) : (
                  <div className="server-error-msg">
                    --No proposed family details available--
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ================= NOMINEE ================= */}

          {shouldShowSection("NOMINEE") && (
            <>
              <SectionCard
                title="Nominee Information (Current)"
                sectionKey="nominee"
              >
                {Array.isArray(nomineeCurrentData) && nomineeCurrentData.length > 0 ? (
                  nomineeCurrentData.map((member, index) => (
                    <div key={member?.id || index}>
                      <div className="row">
                        <InfoRowPersonal
                          label="Name"
                          value={member?.nomineeName}
                        />

                        <InfoRowPersonal
                          label="Relationship"
                          value={member?.relation}
                        />

                        <InfoRowPersonal
                          label="DOB"
                          value={formatDate(member?.dob)}
                        />

                        <InfoRowPersonal
                          label="Aadhaar"
                          value={member?.aadhaarNo}
                        />

                        <InfoRowPersonal
                          label="PAN"
                          value={member?.panNo}
                        />

                        <InfoRowPersonal
                          label="Address"
                          value={member?.address}
                        />
                      </div>

                      <hr />
                    </div>
                  )
                  )) : (
                  <div className="text-muted text-center py-2">
                    No current nominee details available
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Nominee Information (Proposed)"
                sectionKey="nominee"
              >
                {Array.isArray(nomineeProposedData) && nomineeProposedData.length > 0 ? (
                  nomineeProposedData.map((member, index) => (
                    <div key={member?.id || index}>
                      <div className="row">
                        <InfoRowPersonal
                          label="Name"
                          value={member?.name || member?.nomineeName}
                        />

                        <InfoRowPersonal
                          label="Relationship"
                          value={member?.relation}
                        />

                        <InfoRowPersonal
                          label="DOB"
                          value={formatDate(member?.dob)}
                        />

                        <InfoRowPersonal
                          label="Aadhaar"
                          value={member?.aadhaarNo || member?.aadhaar}
                        />

                        <InfoRowPersonal
                          label="PAN"
                          value={member?.panNo || member?.pan}
                        />

                        <InfoRowPersonal
                          label="Address"
                          value={member?.address}
                        />
                      </div>

                      <hr />
                    </div>
                  ))
                ) : (
                  <div className="server-error-msg">
                    --No proposed nominee details available--
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ================= EDUCATION ================= */}

          {shouldShowSection("EDUCATION") && (
            <>
              <SectionCard
                title="Education Details (Current)"
                sectionKey="education"
              >
                {Array.isArray(educationCurrentData) && educationCurrentData.length > 0 ? (
                  educationCurrentData.map((education, index) => (
                    <div key={education?.id || index}>
                      <div className="row">
                        <InfoRowPersonal
                          label="Qualification"
                          value={
                            <>
                              {education?.degreeName}
                              <span className="badge text-dark ms-2">
                                Grade/Cgpa: {education?.gradeOrcgpa}
                              </span>
                            </>
                          }
                        />

                        <InfoRowPersonal
                          label="Institute"
                          value={
                            <>
                              {education?.institutionName}
                              <span className="badge text-dark ms-2">
                                {education?.location}
                              </span>
                            </>
                          }
                        />

                        <InfoRowPersonal
                          label="Field Of Study"
                          value={education?.fieldOfStudy}
                        />

                        <InfoRowPersonal
                          label="Duration"
                          value={getFormattedDuration(
                            education?.fromDate,
                            education?.toDate
                          )}
                        />

                        <InfoRowPersonal
                          label="Roll Number"
                          value={education?.rollNumber}
                        />
                      </div>

                      <hr />
                    </div>
                  )
                  )) : (
                  <div className="text-muted text-center py-2">
                    No current educational details available
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Education Details (Proposed)"
                sectionKey="education"
              >
                {Array.isArray(educationProposedData) && educationProposedData.length > 0 ? (
                  educationProposedData.map((education, index) => (
                    <div key={education?.id || index}>
                      <div className="row">
                        <InfoRowPersonal
                          label="Qualification"
                          value={
                            <>
                              {education?.degreeName}
                              {education?.gradeOrcgpa && (
                                <span className="badge text-dark ms-2">
                                  Grade/Cgpa: {education?.gradeOrcgpa}
                                </span>
                              )}
                            </>
                          }
                        />

                        <InfoRowPersonal
                          label="Institute"
                          value={
                            <>
                              {education?.institutionName}
                              {education?.location && (
                                <span className="badge text-dark ms-2">
                                  {education?.location}
                                </span>
                              )}
                            </>
                          }
                        />

                        <InfoRowPersonal
                          label="Field Of Study"
                          value={education?.fieldOfStudy}
                        />

                        <InfoRowPersonal
                          label="Duration"
                          value={getFormattedDuration(
                            education?.fromDate,
                            education?.toDate
                          )}
                        />

                        <InfoRowPersonal
                          label="Roll Number"
                          value={education?.rollNumber}
                        />
                      </div>

                      <hr />
                    </div>
                  ))
                ) : (
                  <div className="server-error-msg">
                    --No proposed educational details available--
                  </div>
                )}
              </SectionCard>
            </>
          )}
          {/* ================= EXPERIENCE ================= */}

          {shouldShowSection("EXPERIENCE") && (
            <>
              <SectionCard
                title="Experience (Current)"
                sectionKey="experience"
              >
                {Array.isArray(experienceCurrentData) && experienceCurrentData.length > 0 ? (
                  experienceCurrentData.map((exp, index) => (
                    <div key={exp?.id || index}>
                      <div className="row">
                        <InfoRowPersonal
                          label="Employer"
                          value={exp?.previousEmployerName}
                        />

                        <InfoRowPersonal
                          label="Designation"
                          value={exp?.designation}
                        />

                        <InfoRowPersonal
                          label="Emp Code:"
                          value={exp?.employeeCode}
                          className="exp-empcode"
                        />

                        <InfoRowPersonal
                          label="Department"
                          value={exp?.department}
                        />
                        <InfoRowPersonal
                          label="Manager Name:"
                          value={exp?.managerName}
                          className="exp-manager"
                        />
                        <InfoRowPersonal
                          label="Manager Contact:"
                          value={
                            // If BOTH mobile number and email are missing, pass "" to trigger your N/A logic
                            !(exp?.mobileNumber || exp?.managerEmail) ? "" : (
                              <span className="badge text-dark exp-manager-contact bg-light border">
                                {[exp?.mobileNumber, exp?.managerEmail].filter(Boolean).join(" | ")}
                              </span>
                            )
                          }
                          className="exp-manager"
                        />
                        <InfoRowPersonal
                          label="Duration"
                          value={getFormattedDuration(
                            exp?.employmentPeriodFrom,
                            exp?.employmentPeriodTo
                          )}
                        />
                      </div>

                      <hr />
                    </div>
                  )
                  )) : (
                  <div className="text-muted text-center py-2">
                    No current experience details available
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Experience (Proposed)"
                sectionKey="experience"
              >
                {Array.isArray(experienceProposedData) && experienceProposedData.length > 0 ? (
                  experienceProposedData.map((exp, index) => (
                    <div key={exp?.id || index}>
                      <div className="row">
                        <InfoRowPersonal
                          label="Employer"
                          value={exp?.previousEmployerName}
                        />

                        <InfoRowPersonal
                          label="Designation"
                          value={exp?.designation}
                        />

                        <InfoRowPersonal
                          label="Emp Code:"
                          value={exp?.employeeCode}
                          className="exp-empcode"
                        />

                        <InfoRowPersonal
                          label="Department"
                          value={exp?.department}
                        />
                        <InfoRowPersonal
                          label="Manager Name:"
                          value={exp?.managerName}
                          className="exp-manager"
                        />
                        <InfoRowPersonal
                          label="Manager Contact:"
                          value={
                            // If BOTH mobile number and email are missing, pass "" to trigger your N/A logic
                            !(exp?.mobileNumber || exp?.managerEmail) ? "" : (
                              <span className="badge text-dark exp-manager-contact bg-light border">
                                {[exp?.mobileNumber, exp?.managerEmail].filter(Boolean).join(" | ")}
                              </span>
                            )
                          }
                          className="exp-manager"
                        />

                        <InfoRowPersonal
                          label="Duration"
                          value={getFormattedDuration(
                            exp?.employmentPeriodFrom,
                            exp?.employmentPeriodTo
                          )}
                        />
                      </div>

                      <hr />
                    </div>
                  ))
                ) : (
                  <div className="server-error-msg">
                    --No proposed experience details available--
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ================= ADDRESS ================= */}

          {shouldShowSection("ADDRESS") && (
            <>
              {/* --- CURRENT ADDRESS CARD  --- */}
              <SectionCard
                title="Address (Current)"
                sectionKey="address"
              >
                {(() => {
                  const currentDataArray = (Array.isArray(addressCurrentData) ? addressCurrentData : []);

                  // Check if we have valid address fields instead of just relying on addressType
                  const validCurrentAddresses = currentDataArray.filter(
                    address => address?.address1 || address?.city || address?.zip
                  );

                  if (validCurrentAddresses.length > 0) {
                    return validCurrentAddresses.map((address, index) => (
                      <div key={address?.id || index} className="d-flex align-items-center justify-content-between mb-2 unique-personal-row-item">
                        <span className="d-inline-flex align-items-center unique-personal-label">{`${address?.addressType || "CURRENT"}`}</span>
                        <p className="text-dark m-0 unique-personal-value text-right">{joinAddress(address)}</p>
                        {index < validCurrentAddresses.length - 1 && <hr />}
                      </div>
                    ));
                  }

                  return <div className="server-error-msg">--No details found--</div>;
                })()}
              </SectionCard>

              {/* --- PROPOSED ADDRESS CARD --- */}
              <SectionCard
                title="Address (Proposed)"
                sectionKey="address"
              >
                {(() => {
                  const structuralData = Array.isArray(addressProposedData?.addresses)
                    ? addressProposedData.addresses
                    : (Array.isArray(addressProposedData) ? addressProposedData : []);

                  // REMOVED the strict !== "PERMANENT" filter that was dropping your backend data
                  if (structuralData.length > 0) {
                    return structuralData.map((address, index) => (
                      <div key={address?.id || index} className="d-flex align-items-center justify-content-between mb-2 unique-personal-row-item">
                        <span className="d-inline-flex align-items-center unique-personal-label">{`${address?.addressType || "CURRENT"}`}</span>
                        <p className="text-dark m-0 unique-personal-value text-right">{joinAddress(address)}</p>
                        {index < structuralData.length - 1 && <hr />}
                      </div>
                    ));
                  }

                  return <div className="server-error-msg">--No details found--</div>;
                })()}
              </SectionCard>
            </>
          )}

          {/* ================= ADDITIONAL ================= */}

          {shouldShowSection("ADDITIONAL_DETAILS") && (
            <>
              <SectionCard title="Additional Information (Current)" sectionKey="additional" >
                <div className="row">
                  <InfoRowPersonal
                    label="Passport Number"
                    value={additionalCurrentData?.passportNumber}
                  />

                  <InfoRowPersonal
                    label="Hobbies"
                    value={additionalCurrentData?.hobbiesDeclaration}
                  />

                  <InfoRowPersonal
                    label="Illness Declaration"
                    value={additionalCurrentData?.illnessDeclaration}
                  />
                </div>
              </SectionCard>

              <SectionCard title="Additional Information (Proposed)" sectionKey="additional" >
                <div className="row">
                  <InfoRowPersonal
                    label="Passport Number"
                    value={additionalProposedData?.passportNumber}
                  />

                  <InfoRowPersonal
                    label="Hobbies"
                    value={additionalProposedData?.hobbiesDeclaration}
                  />

                  <InfoRowPersonal
                    label="Illness Declaration"
                    value={additionalProposedData?.illnessDeclaration}
                  />
                </div>
              </SectionCard>
            </>
          )}

          {/* ================= PROFESSIONAL_REFERENCE ================= */}

          {shouldShowSection("PROFESSIONAL_REFERENCE") && (
            <>
              <SectionCard title="Professional Reference (Current)" sectionKey="professional" >
                <div className="row">
                  <InfoRowPersonal
                    label="Name"
                    value={referenceCurrentData?.[0]?.name}
                  />

                  <InfoRowPersonal
                    label="Email"
                    value={referenceCurrentData?.[0]?.email}
                  />

                  <InfoRowPersonal
                    label="Contact"
                    value={referenceCurrentData?.[0]?.contactNumber}
                  />

                  <InfoRowPersonal
                    label="Designation"
                    value={referenceCurrentData?.[0]?.designation}
                  />

                  <InfoRowPersonal
                    label="Relationship"
                    value={referenceCurrentData?.[0]?.relationship}
                  />

                  <InfoRowPersonal
                    label="Employee ID"
                    value={referenceCurrentData?.[0]?.employeeId}
                  />

                  <InfoRowPersonal
                    label="Department"
                    value={referenceCurrentData?.[0]?.department}
                  />
                </div>
              </SectionCard>

              <SectionCard title="Professional Reference (Proposed)" sectionKey="professional" >
                <div className="row">
                  <InfoRowPersonal
                    label="Name"
                    value={referenceProposedData?.[0]?.name}
                  />

                  <InfoRowPersonal
                    label="Email"
                    value={referenceProposedData?.[0]?.email}
                  />

                  <InfoRowPersonal
                    label="Contact"
                    value={referenceProposedData?.[0]?.contactNumber}
                  />

                  <InfoRowPersonal
                    label="Designation"
                    value={referenceProposedData?.[0]?.designation}
                  />

                  <InfoRowPersonal
                    label="Relationship"
                    value={referenceProposedData?.[0]?.relationship}
                  />

                  <InfoRowPersonal
                    label="Employee ID"
                    value={referenceProposedData?.[0]?.employeeId}
                  />

                  <InfoRowPersonal
                    label="Department"
                    value={referenceProposedData?.[0]?.department}
                  />
                </div>
              </SectionCard>
            </>
          )}
        </>
      )}

    </div>
  );
};

ProfileUpdateSection.propTypes = {
  hasCategory: PropTypes.func.isRequired,
  openSections: PropTypes.object.isRequired,
  toggleSection: PropTypes.func.isRequired,
  InfoRow: PropTypes.elementType.isRequired,
  InfoRowPersonal: PropTypes.elementType.isRequired,
};

export default ProfileUpdateSection;